import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Header from "../common/layout/header";
import Footer from "../common/layout/footer";
import { http } from "../helper";
import { BaseUrl } from "../common/config";
import CommonAlert from "../common/Commonalert";
import Sidebar from "../common/sidebar";
import ShowAlert from "../common/Showalert";
import { menuPermissions } from "../account/menupermissions";
import { canAction } from "../common/permissionhelper";
import { useTranslation } from "react-i18next";
const MODULE = "manage_roles";

const EditRoles = () => {
  const { t } = useTranslation();
  const canEdit = canAction(MODULE, "edit");
  const isReadOnly = !canEdit; // if true, disable inputs and hide submit
  const [roleName, setRoleName] = useState("");
  const [preroleName, setPreroleName] = useState("");
  const [roleList, setRoleList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPermissions, setSelectedPermissions] = useState([]); // main modules
  const [selectedSubPermissions, setSelectedSubPermissions] = useState({}); // sub toggles
  const [selectedReports, setSelectedReports] = useState({});
  const [errors, setErrors] = useState({});
  const [alertPopup, setAlertPopup] = useState(false);
  const [alertMessage, setAlertMessage] = useState([]);
  const [alerterrorPopup, setAlertErrorPopup] = useState(false);
  const [alerterrMessage, setAlerterrMessage] = useState([]);
  const params = useParams();

  if (!sessionStorage.getItem("isLoggedIn")) window.location.href = "/";

  useEffect(() => {
    // Fetch role details
    const fetchPermissions = async () => {
      try {
        let URL = BaseUrl + `admin/getRole/${params.id}`;
        const response = await http.get(URL);
        const data = response.data.data;
        setRoleName(data.roleName);
        setPreroleName(data.roleName);
        // split main and sub-permissions
        const mainPerms = [];
        const subPerms = {};
        data.permissions.forEach((p) => {
          if (p.includes(".")) {
            // Dashboard perms are 3-part: manage_dashboard.revenueorder.view
            // Other perms are 2-part: manage_products.view
            const parts = p.split(".");
            const mainKey = parts[0]; // e.g. manage_dashboard or manage_products
            if (!subPerms[mainKey]) subPerms[mainKey] = [];
            subPerms[mainKey].push(p);
          } else {
            mainPerms.push(p);
          }
        });
        setSelectedPermissions(mainPerms);
        setSelectedSubPermissions(subPerms);
        // Reconstruct selectedReports from saved dashboard sub-permissions
        if (mainPerms.includes('manage_dashboard')) {
          const dashMod = menuPermissions.find((m) => m.value === 'manage_dashboard');
          const savedDashPerms = subPerms['manage_dashboard'] || [];
          const reportState = {};
          dashMod?.reportGroups?.forEach((rg) => {
            // A report is considered checked if it has at least a view perm saved
            const hasViewPerm = savedDashPerms.some((p) => p.startsWith(rg.value + '.'));
            reportState[rg.value] = hasViewPerm;
          });
          setSelectedReports(reportState);
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching permissions:", error);
      }
      // fetch role list
      try {
        let URL1 = BaseUrl + `admin/getRoles/`;
        const response = await http.get(URL1);
        setRoleList(response.data.data || []);
      } catch (err) {
        console.log(err);
      }
    };
    fetchPermissions();
  }, []);

  // Helpers
  const getDefaultSubPerms = (moduleValue) => {
    const mod = menuPermissions.find((m) => m.value === moduleValue);
    if (!mod || !mod.subPermissions) return [];
    return mod.subPermissions.filter((s) => s.default).map((s) => s.value);
  };
  // Module-level toggle
  const toggleModule = (moduleValue, checked) => {
    const isDashboard = moduleValue === 'manage_dashboard';
    if (checked) {
      setSelectedPermissions((prev) =>
        Array.from(new Set([...prev, moduleValue])),
      );
      if (isDashboard) {
        // Select ALL report groups and enable view permission for each
        const dashMod = menuPermissions.find((m) => m.value === 'manage_dashboard');
        const allReports = dashMod?.reportGroups || [];
        const allReportSelected = {};
        const allViewPerms = [];
        allReports.forEach((r) => {
          allReportSelected[r.value] = true;
          const viewPerm = r.permissions.find((p) => p.value.endsWith('.view'));
          if (viewPerm) allViewPerms.push(viewPerm.value);
        });
        setSelectedReports(allReportSelected);
        setSelectedSubPermissions((prev) => ({
          ...prev,
          [moduleValue]: allViewPerms,
        }));
      } else {
        setSelectedSubPermissions((prev) => {
          if (prev[moduleValue] && prev[moduleValue].length) return prev;
          return { ...prev, [moduleValue]: getDefaultSubPerms(moduleValue) };
        });
      }
      setErrors((prev) => ({ ...prev, permissions: "" }));
    } else {
      setSelectedPermissions((prev) => prev.filter((p) => p !== moduleValue));
      setSelectedSubPermissions((prev) => {
        const copy = { ...prev };
        delete copy[moduleValue];
        return copy;
      });
      if (isDashboard) setSelectedReports({});
    }
  };
  // Sub-permission toggle (non-dashboard modules)
  const toggleSubPermission = (moduleValue, subValue, checked) => {
    setSelectedSubPermissions((prev) => {
      const curr = new Set(prev[moduleValue] || []);
      if (checked) curr.add(subValue);
      else curr.delete(subValue);
      return { ...prev, [moduleValue]: Array.from(curr) };
    });
  };
  // Dashboard: toggle a report group checkbox
  const toggleReport = (reportValue, checked, reportGroup) => {
    setSelectedReports((prev) => ({ ...prev, [reportValue]: checked }));
    if (checked) setErrors((prev) => ({ ...prev, dashboardReports: "" }));
    setSelectedSubPermissions((prev) => {
      const currentPerms = new Set(prev['manage_dashboard'] || []);
      if (checked) {
        const viewPerm = reportGroup.permissions.find((p) => p.value.endsWith('.view'));
        if (viewPerm) currentPerms.add(viewPerm.value);
      } else {
        reportGroup.permissions.forEach((p) => currentPerms.delete(p.value));
      }
      return { ...prev, manage_dashboard: Array.from(currentPerms) };
    });
  };
  // Dashboard: toggle view/export within a report
  const toggleReportPermission = (reportGroup, permValue, checked) => {
    const isViewPerm = permValue.endsWith('.view');
    const exportPerm = reportGroup.permissions.find((p) => p.value.endsWith('.export'))?.value;
    setSelectedSubPermissions((prev) => {
      const currentPerms = new Set(prev['manage_dashboard'] || []);
      if (checked) {
        currentPerms.add(permValue);
      } else {
        currentPerms.delete(permValue);
        // Disabling View must also disable Export
        if (isViewPerm && exportPerm) currentPerms.delete(exportPerm);
      }
      return { ...prev, manage_dashboard: Array.from(currentPerms) };
    });
    // If View is turned off, also uncheck the report checkbox itself
    if (isViewPerm && !checked) {
      setSelectedReports((prev) => ({ ...prev, [reportGroup.value]: false }));
    }
  };
  // Validation
  const formvalidation = () => {
    const newErrors = {};
    let isValid = true;
    if (roleName.trim() === "") {
      newErrors.roleName = t("role_name_required_txt");
      isValid = false;
    }
    if (selectedPermissions.length === 0) {
      newErrors.permissions = t("select_at_least_one_permission_txt");
      isValid = false;
    }
    // If Dashboard is selected, at least one report group must be checked
    if (selectedPermissions.includes('manage_dashboard')) {
      const anyReportSelected = Object.values(selectedReports).some(Boolean);
      if (!anyReportSelected) {
        newErrors.dashboardReports = t("select_at_least_one_report_txt");
        isValid = false;
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  const btnsubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;
    if (!formvalidation()) return;
    // Build payload: main + selected sub-permissions only if main is selected
    let permissions = [];
    selectedPermissions.forEach((mod) => {
      permissions.push(mod);
      const subs = selectedSubPermissions[mod] || [];
      permissions = permissions.concat(subs);
    });
    permissions = Array.from(new Set(permissions)); // dedupe
    const payload = { id: params.id, roleName, permissions };
    // check duplicate role name
    const isFound =
      preroleName.toLowerCase() !== roleName.toLowerCase() &&
      roleList.some(
        (el) => el.roleName.toLowerCase() === roleName.toLowerCase(),
      );
    if (isFound) {
      setAlertErrorPopup(true);
      setAlerterrMessage(t("role_name_already_exists_txt"));
      return;
    }
    try {
      setIsLoading(true);
      const URL = BaseUrl + "admin/editRole";
      const response = await http.put(URL, payload);
      if (response.data.success === true) {
        setAlertPopup(true);
        setAlertMessage(t("role_permissions_updated_successfully_txt"));
      } else {
        setAlertErrorPopup(true);
        setAlerterrMessage(response.data.message || t("failed_to_update_role_txt"));
      }
    } catch (err) {
      console.log(err);
      setAlertErrorPopup(true);
      setAlerterrMessage(t("server_error_updating_role_txt"));
    } finally {
      setIsLoading(false);
    }
  };

  const routeChange = () => {
    window.location.href = `/role/`;
  };

  return (
    <section className="dashboard-block">
      {alerterrorPopup && <ShowAlert title={t("alert_txt")} message={alerterrMessage} />}
      {alertPopup && <CommonAlert title={t("alert_txt")} message={alertMessage} />}
      <Sidebar />
      <div className="dash-left">
        <Header />
        <section className="cart-block">
          <div className="container account-details">
            <h1 className="checkout-title">
              {canEdit
                ? t("edit_role_permissions_txt")
                : t("view_role_permissions_txt")}
            </h1>
            <div className="right-table-outer-wrap">
            {isLoading ? (
              <div className="loading-buffer"></div>
            ) : (
              <div className="form-field freeproduct-fieldset addroles-fieldset form-container-xs">
                <form className="client-detail-form" onSubmit={btnsubmit}>
                  {/* Role Name */}
                  <div className="input-field">
                    <label>{t("role_name_txt")}</label>
                    <input
                      type="text"
                      value={roleName}
                      onChange={(e) => {
                        setRoleName(e.target.value);
                        setErrors((prev) => ({ ...prev, roleName: "" }));
                      }}
                      readOnly={isReadOnly}
                    />
                    {errors.roleName && (
                      <div className="error-msg">{errors.roleName}</div>
                    )}
                  </div>
                  {/* Permissions */}
                  <div className="input-field">
                    <label>{t("permissions_txt")}</label>
                    <div className="custom_check-box">
                      {menuPermissions
                        .filter(
                          (module) =>
                            !isReadOnly ||
                            selectedPermissions.includes(module.value),
                        ) // show only permitted modules in read-only
                        .map((module, index) => {
                          const modVal = module.value;
                          const isDashboard = modVal === 'manage_dashboard';
                          const moduleChecked = 
                            selectedPermissions.includes(modVal);
                          const subSelected =
                            selectedSubPermissions[modVal] || [];
                          return (
                            <div className="check-box" key={modVal}>
                              {/* Module checkbox */}
                              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                <input
                                  id={`chkbox${index}`}
                                  type="checkbox"
                                  value={modVal}
                                  checked={moduleChecked}
                                  onChange={(e) => {
                                    if (isReadOnly) return;
                                    toggleModule(modVal, e.target.checked);
                                  }}
                                />
                                <label htmlFor={`chkbox${index}`}>
                                  {t(module.name)}
                                </label>
                              </div>
                                {/* Dashboard: hierarchical report groups */}
                                {moduleChecked && isDashboard && module.reportGroups && (
                                  <div className="rolls-action-bx" style={{ marginTop: "8px", paddingLeft: "24px" }}>
                                    {module.reportGroups
                                      .filter((rg) =>
                                        !isReadOnly || !!selectedReports[rg.value],
                                      )
                                      .map((reportGroup) => {
                                        const reportChecked = !!selectedReports[reportGroup.value];
                                        const viewPermVal   = reportGroup.permissions.find((p) => p.value.endsWith('.view'))?.value;
                                        const exportPermVal = reportGroup.permissions.find((p) => p.value.endsWith('.export'))?.value;
                                        const viewChecked   = subSelected.includes(viewPermVal);
                                        const exportChecked = subSelected.includes(exportPermVal);
                                        return (
                                          <div key={reportGroup.value} style={{ marginBottom: "10px" }}>

                                            {/* Report group checkbox */}
                                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                              <input
                                                id={`rpt-${reportGroup.value}`}
                                                type="checkbox"
                                                checked={reportChecked}
                                                //disabled={isReadOnly}
                                                onChange={(e) => {
                                                  if (!isReadOnly)
                                                    toggleReport(reportGroup.value, e.target.checked, reportGroup);
                                                }}
                                              />
                                              <label htmlFor={`rpt-${reportGroup.value}`} style={{ fontWeight: 500 }}>
                                                {t(reportGroup.name)}
                                              </label>
                                            </div>
                                            {/* View / Export toggles */}
                                            {reportChecked && (
                                              <div
                                                className="switch-group"
                                                style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "6px", paddingLeft: "28px" }}
                                              >
                                                {/* View toggle */}
                                                <div className="switch-item" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                  <span className="switch-label">{t("view_txt")}</span>
                                                  <label className="switch switch-ios">
                                                    <input
                                                      type="checkbox"
                                                      checked={viewChecked}
                                                      disabled={isReadOnly}
                                                      onChange={(e) => {
                                                        if (!isReadOnly)
                                                          toggleReportPermission(reportGroup, viewPermVal, e.target.checked);
                                                      }}
                                                    />
                                                    <span className="slider-ios"></span>
                                                  </label>
                                                </div>
                                                {/* Export toggle — disabled when view is off */}
                                                {(!isReadOnly || exportChecked) && (
                                                  <div className="switch-item" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                                    <span className="switch-label">{t("export_btn_txt")}</span>
                                                    <label className="switch switch-ios">
                                                      <input
                                                        type="checkbox"
                                                        checked={exportChecked}
                                                        disabled={isReadOnly || !viewChecked}
                                                        onChange={(e) => {
                                                          if (!isReadOnly)
                                                            toggleReportPermission(reportGroup, exportPermVal, e.target.checked);
                                                        }}
                                                      />
                                                      <span className="slider-ios"></span>
                                                    </label>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                                {/* Dashboard report validation error */}
                                {moduleChecked && isDashboard && errors.dashboardReports && (
                                  <div className="error-msg" style={{ paddingLeft: "24px", marginTop: "4px" }}>
                                    {errors.dashboardReports}
                                  </div>
                                )}
                                {/* All other modules: flat sub-permission toggles */}
                                {moduleChecked && !isDashboard && (() => {
                                  const subsToShow = isReadOnly
                                    ? module.subPermissions?.filter((sub) => subSelected.includes(sub.value)) || []
                                    : module.subPermissions || [];
                                  return subsToShow.length > 0 ? (
                                    <div className="rolls-action-bx" style={{ marginTop: "8px" }}>
                                      <div className="switch-group" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                                        {subsToShow.map((sub) => {
                                          const subChecked   = subSelected.includes(sub.value);
                                          const isViewToggle = sub.value.endsWith(".view") || sub.value.endsWith(".refresh");
                                          return (
                                            <div
                                              className="switch-item"
                                              key={sub.value}
                                              style={{ display: "flex", alignItems: "center", gap: "6px" }}
                                            >
                                              <span className="switch-label">{t(sub.name)}</span>
                                              <label className="switch switch-ios">
                                                <input
                                                  type="checkbox"
                                                  checked={subChecked}
                                                  disabled={isReadOnly || isViewToggle}
                                                  onChange={(e) => {
                                                    if (!isReadOnly && !isViewToggle)
                                                      toggleSubPermission(modVal, sub.value, e.target.checked);
                                                  }}
                                                />
                                                <span className="slider-ios"></span>
                                              </label>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ) : null;
                                })()}
                              </div>
                            );
                          })}
                      </div>
                      {errors.permissions && (
                        <p className="error-msg">{errors.permissions}</p>
                      )}
                    </div>
                    {/* Buttons */}
                    <div className="btnfield" style={{ marginTop: "16px" }}>
                      {canEdit && (
                        <button title={t("update_role_txt")} type="submit" disabled={isLoading}>
                          {t("update_role_txt")}
                        </button>
                      )}
                      &nbsp;&nbsp;&nbsp;
                      <button title={t("cancel_txt")} type="button" onClick={routeChange}>
                        {t("cancel_txt")}
                      </button>
                    </div>

                  </form>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </section>
      </div>
    </section>
  );
};

export default EditRoles;
